import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as dashboardActions from "src/redux/slices/dashboardSlice";
import AreaChartWrapper from "src/components/ui/chart/AreaChartWrapper";
import Skeleton from "src/components/ui/Skeleton";
import { formatDateTime } from "src/utils/date-time";

interface StockMovementCardProps {
  days: number;
  periodLabel: string;
  enabled?: boolean;
  warehouseId?: string;
}

const StockMovementCard: React.FC<StockMovementCardProps> = ({
  days,
  periodLabel,
  enabled = true,
  warehouseId,
}) => {
  const dispatch = useAppDispatch();
  const { tenantStockMovement } = useAppSelector((state) => state.dashboard);

  useEffect(() => {
    if (!enabled) return;
    dispatch(
      dashboardActions.getTenantStockMovementRequest({
        days,
        warehouseId: warehouseId || undefined,
      })
    );
  }, [dispatch, days, enabled, warehouseId]);

  if (!enabled) return null;

  const data = tenantStockMovement.data?.data || [];
  const isEmpty =
    data.length === 0 ||
    data.every((d) => !(d.stockIn || 0) && !(d.stockOut || 0));

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
          Stock in vs. Stock Out
        </h2>
        <span className="text-xs text-gray-400 dark:text-gray-500">{periodLabel}</span>
      </div>
      {tenantStockMovement.loading ? (
        <Skeleton className="mt-4 h-44 w-full" />
      ) : (
        <AreaChartWrapper
          data={data}
          hideYAxis
          rotateXAxisLabels
          xTickFormatter={(value) => formatDateTime(value, "custom", "DD MMM") || value}
          isEmpty={isEmpty}
          emptyMessage={`No stock movement in the last ${days} days`}
        />
      )}
    </div>
  );
};

export default StockMovementCard;
